import { useState } from 'react';
import { Link } from 'react-router';
import { Check, X, ArrowRight } from 'lucide-react';
import { Section } from '../components/section';

type Plan = 'starter' | 'standard' | 'premium';

interface FeatureRow {
  feature: string;
  starter: boolean | string;
  standard: boolean | string;
  premium: boolean | string;
  group?: string;
}

const ROWS: FeatureRow[] = [
  { group: 'Core', feature: 'Students module (admissions, records)', starter: true, standard: true, premium: true },
  { group: 'Core', feature: 'Fees module (collection, receipts, dues)', starter: true, standard: true, premium: true },
  { group: 'Core', feature: 'Finance module (vouchers, ledger, reports)', starter: true, standard: true, premium: true },
  { group: 'Core', feature: 'Payroll module', starter: false, standard: true, premium: true },
  { group: 'Core', feature: 'Library module', starter: false, standard: true, premium: true },

  { group: 'Capacity', feature: 'Active students', starter: 'Up to 250', standard: 'Up to 1,000', premium: 'Unlimited' },
  { group: 'Capacity', feature: 'Staff accounts', starter: 'Up to 10', standard: 'Up to 40', premium: 'Unlimited' },
  { group: 'Capacity', feature: 'Academic years of history', starter: '3 years', standard: '7 years', premium: 'Unlimited' },

  { group: 'Communication', feature: 'Email notifications (Brevo)', starter: true, standard: true, premium: true },
  { group: 'Communication', feature: 'WhatsApp dispatch', starter: false, standard: true, premium: true },
  { group: 'Communication', feature: 'SMS dispatch', starter: false, standard: true, premium: true },
  { group: 'Communication', feature: 'Parent portal app access', starter: true, standard: true, premium: true },

  { group: 'Payments', feature: 'Razorpay online fee collection', starter: false, standard: true, premium: true },
  { group: 'Payments', feature: 'Custom payment gateway', starter: false, standard: false, premium: true },

  { group: 'Support', feature: 'Email support', starter: '48-hour', standard: '24-hour', premium: 'Priority' },
  { group: 'Support', feature: 'Onboarding session', starter: 'Self-serve', standard: 'Guided (1 hr)', premium: 'Dedicated' },
  { group: 'Support', feature: 'Dedicated success manager', starter: false, standard: false, premium: true },
];

const PRICES: Record<Plan, { monthly: string; annual: string }> = {
  starter: { monthly: '₹4,999', annual: '₹3,999' },
  standard: { monthly: '₹9,999', annual: '₹7,999' },
  premium: { monthly: 'Custom', annual: 'Custom' },
};

export function PricingPage() {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual');

  return (
    <>
      <header className="mx-auto max-w-6xl px-6 pb-10 pt-20 md:pt-24">
        <div className="mx-auto max-w-2xl text-center">
          <p
            className="text-[11px] font-semibold uppercase"
            style={{ color: 'var(--color-accent-600)', letterSpacing: '0.10em' }}
          >
            Pricing
          </p>
          <h1
            className="mt-3"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3.25rem)',
              lineHeight: 1.05,
              fontWeight: 700,
              letterSpacing: '-0.032em',
            }}
          >
            Simple plans. No surprises.
          </h1>
          <p className="mt-5 text-[16px] leading-relaxed" style={{ color: 'var(--fg-mid)' }}>
            Pick a plan that fits your school today — switch up or down any time. All
            plans include hosting, backups and updates.
          </p>

          <BillingToggle value={billing} onChange={setBilling} />
        </div>
      </header>

      <Section>
        <div
          className="overflow-hidden rounded-xl"
          style={{ border: '1px solid var(--border-line)', background: 'var(--bg-surface)' }}
        >
          <table className="w-full text-left" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr>
                <th
                  className="px-6 py-6"
                  style={{ width: '34%', borderBottom: '1px solid var(--border-line)' }}
                >
                  <p
                    className="text-[11px] font-semibold uppercase"
                    style={{ color: 'var(--fg-low)', letterSpacing: '0.08em' }}
                  >
                    Compare plans
                  </p>
                </th>
                {(['starter', 'standard', 'premium'] as Plan[]).map((p) => {
                  const isHighlight = p === 'standard';
                  return (
                    <th
                      key={p}
                      className="px-6 py-6 text-left"
                      style={{
                        width: '22%',
                        borderBottom: '1px solid var(--border-line)',
                        background: isHighlight
                          ? 'color-mix(in oklab, var(--color-accent-500) 6%, var(--bg-surface))'
                          : undefined,
                      }}
                    >
                      <p
                        className="text-[12px] font-semibold uppercase"
                        style={{ color: 'var(--fg-low)', letterSpacing: '0.08em' }}
                      >
                        {p}
                      </p>
                      <p
                        className="mt-2"
                        style={{
                          fontSize: '1.75rem',
                          fontWeight: 700,
                          letterSpacing: '-0.028em',
                          color: 'var(--fg-high)',
                        }}
                      >
                        {PRICES[p][billing]}
                      </p>
                      <p className="text-[12px]" style={{ color: 'var(--fg-low)' }}>
                        {PRICES[p][billing] === 'Custom'
                          ? 'Talk to sales'
                          : billing === 'monthly'
                            ? '/month, billed monthly'
                            : '/month, billed annually'}
                      </p>
                      <Link
                        to="/contact"
                        className={
                          isHighlight
                            ? 'ed-btn-primary ed-btn-sm mt-4 w-full'
                            : 'ed-btn-outline ed-btn-sm mt-4 w-full'
                        }
                      >
                        {p === 'premium' ? 'Contact us' : 'Start trial'}
                      </Link>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) => {
                const prevGroup = ROWS[i - 1]?.group;
                const showGroupHeader = row.group && row.group !== prevGroup;
                return (
                  <FeatureGroupRow
                    key={`${row.group}-${row.feature}`}
                    row={row}
                    showGroupHeader={!!showGroupHeader}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-10 text-center">
          <p className="text-[14px]" style={{ color: 'var(--fg-mid)' }}>
            Have a special case? We&apos;re happy to talk.
          </p>
          <Link
            to="/contact"
            className="mt-4 inline-flex items-center gap-2 text-[13px] font-medium"
            style={{ color: 'var(--color-accent-700)' }}
          >
            Schedule a 20-minute call
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </Section>
    </>
  );
}

function FeatureGroupRow({ row, showGroupHeader }: { row: FeatureRow; showGroupHeader: boolean }) {
  return (
    <>
      {showGroupHeader && (
        <tr>
          <td
            colSpan={4}
            className="px-6 pb-3 pt-7"
            style={{
              background: 'var(--bg-sunken)',
              borderTop: '1px solid var(--border-line)',
              borderBottom: '1px solid var(--border-line)',
            }}
          >
            <p
              className="text-[11px] font-semibold uppercase"
              style={{ color: 'var(--fg-low)', letterSpacing: '0.08em' }}
            >
              {row.group}
            </p>
          </td>
        </tr>
      )}
      <tr style={{ borderTop: '1px solid var(--border-hair)' }}>
        <td className="px-6 py-3.5 text-[13.5px]" style={{ color: 'var(--fg-mid)' }}>
          {row.feature}
        </td>
        {(['starter', 'standard', 'premium'] as Plan[]).map((p) => (
          <td
            key={p}
            className="px-6 py-3.5 text-[13.5px]"
            style={{
              color: 'var(--fg-high)',
              background:
                p === 'standard'
                  ? 'color-mix(in oklab, var(--color-accent-500) 3%, var(--bg-surface))'
                  : undefined,
            }}
          >
            <CellValue value={row[p]} />
          </td>
        ))}
      </tr>
    </>
  );
}

function CellValue({ value }: { value: boolean | string }) {
  if (value === true)
    return (
      <Check
        className="h-4 w-4"
        style={{ color: 'var(--color-accent-500)' }}
        aria-label="Included"
      />
    );
  if (value === false)
    return (
      <X
        className="h-4 w-4"
        style={{ color: 'var(--fg-faint)' }}
        aria-label="Not included"
      />
    );
  return <span>{value}</span>;
}

function BillingToggle({
  value,
  onChange,
}: {
  value: 'monthly' | 'annual';
  onChange: (v: 'monthly' | 'annual') => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Billing cadence"
      className="mt-8 inline-flex items-center gap-1 rounded-full p-1"
      style={{ border: '1px solid var(--border-line)', background: 'var(--bg-surface)' }}
    >
      {(['monthly', 'annual'] as const).map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt)}
            className="rounded-full px-4 py-1.5 text-[12.5px] font-medium transition-colors"
            style={{
              color: active ? 'var(--fg-on-accent)' : 'var(--fg-mid)',
              background: active ? 'var(--color-accent-500)' : 'transparent',
            }}
          >
            {opt === 'monthly' ? 'Monthly' : 'Annual'}
            {opt === 'annual' && !active && (
              <span
                className="ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                style={{
                  background: 'var(--semantic-success-bg)',
                  color: 'var(--semantic-success)',
                }}
              >
                Save 20%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
